export function xor(a: boolean, b: boolean): boolean {
  return (a && !b) || (!a && b);
}

// ration: 0 ~ 1
export function interp(a: number, b: number, ratio: number): number {
  return a + (b - a) * ratio;
}

/**
 * 변수가 정의 안 되어있거나 null인 경우를 확인하는 함수
 */
export function IsEmptyVar(value: any): boolean {
  return typeof value === 'undefined' || value === null;
}


/**
 * 변수가 정의 안 되어있거나 null이거나 빈 문자열인 경우를 확인하는 함수
 */
export function IsEmptyString(value: string): boolean {
  return IsEmptyVar(value) || value.trim() === '';
}


/**
 * 'Y'값이면 True, 기타 값이면 False를 반환.
 * 정의가 안 되어있거나 null인 경우에도 False를 반환.
 */
export function ParseBoolean(value: string): boolean {
  return IsEmptyVar(value) ? false : value === 'Y';
}


export function Txt2Date(time: number): Date {
  return new Date(time);
}


const regExLastNum = '[1-9][0-9]*$';

/**
 * 문자열에서 마지막 숫자를 얻는 함수
 * @param value
 * @return 마지막 숫자. 없으면 0
 */
export function GetLastNumber(value: string): number {
  const match = value.match(regExLastNum);
  return match ? Number(match[0]) : 0;
}

/**
 * 문자열에 https:// 나 http:// 가 포함되었는지 확인하는 함수
 */
export function IsContainHttp(value: string): boolean {
  return /(http(s?)):\/\//i.test(value);
}


/**
 * 오브젝트 비교. 2-depth 까지 가능
 */
export function IsEqualObj1(obj1: Record<string, any>, obj2: Record<string, any>): boolean {
  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);

  if (keys1.length !== keys2.length) {
    return false;
  }

  for (const key of keys1) {
    if (obj1[key] !== obj2[key]) {
      return false;
    }
  }

  return true;
}


/**
 * 두 오브젝트를 비교하는데, 특정 키를 제외하고 비교한다.
 * @param obj1
 * @param obj2
 * @param key
 * @constructor
 */
export function IsEqualExceptKey(obj1: Record<string, any>, obj2: Record<string, any>, key: string) {
  const _copy1 = { ...obj1 };
  const _copy2 = { ...obj2 };

  delete _copy1[key];
  delete _copy2[key];

  return IsEqualObj1(_copy1, _copy2);
}


export function isEqualOrBothUndefined(a?: string, b?: string) {
  return a === b || (!a && !b);
}

/**
 * 한자인지 확인하는 함수
 * 참고: https://bit.ly/2ZKZUov
 *
 * @param char - 확인하려는 1글자 문자열
 */
export function IsHanja(char: string): boolean {
  const regEx = '[\u2e80-\u2eff\u31c0-\u31ef\u3200-\u32ff\u3400-\u4dbf\u4e00-\u9fbf\uf900-\ufaff]';
  return !!char.match(regEx);
}


export function SafeClearTimeout(handle: number|null): void {
  if (handle !== null) {
    clearTimeout(handle);
  }
}


/**
 * enum 값에서 값 이름을 문자열로 받아오는 함수.
 * @param myEnum
 * @param enumValue
 */
export function getEnumNameByValue(myEnum: any, enumValue: number): string | null {
  let keys = Object.keys(myEnum).filter(x => myEnum[x] == enumValue);
  return keys.length > 0 ? keys[0] : null;
}


export function GenPassword(passwordLength: number = 12): string {
  const chars = '0123456789abcdefghijklmnopqrstuvwxyz!@#$%^&*()ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  let password = '';
  for (let i = 0; i <= passwordLength; i++) {
    const randomNumber = Math.floor(Math.random() * chars.length);
    password += chars.substring(randomNumber, randomNumber + 1);
  }
  return password;
}


/**
 * 두 버전 문자열을 비교하는 함수
 * 버전 형식은 '1.2.3'과 같은 점으로 구분된 숫자 형식
 * NOTE: 점의 갯수가 다르면 적은 쪽의 숫자 갯수만 비교
 * 
 * @param v1 비교할 첫 번째 버전 문자열
 * @param v2 비교할 두 번째 버전 문자열
 * @returns 비교 결과: v1이 더 크면 양수, v2가 더 크면 음수, 동일하면 0 반환
 */
export function CmpVersion(v1: string, v2: string): number {
  // 버전 문자열을 점(.)으로 분리하여 숫자 배열로 변환하는 함수
  const ver2list = (v: string): Array<number> => v.split('.').map(value => parseInt(value, 10));
  const v1list = ver2list(v1);
  const v2list = ver2list(v2);
  
  // 두 버전 중 더 짧은 쪽의 숫자 갯수만 비교
  const max = Math.min(v1list.length, v2list.length);

  // 각 위치의 숫자를 순차적으로 비교
  for (let i = 0; i < max; ++i) {
    const d = v1list[i] - v2list[i];
    // 숫자가 다르면 즉시 그 차이를 반환
    if (d !== 0) { return d; }
  }
  
  // 모든 공통 자리수가 같은 경우, 더 긴 버전이 없으면 동일함
  return 0;
}


export interface SplitUrlResult {
  address: string;
  params: Record<string, string>;
}


/**
 * URL을 분리하는 함수
 * @param url
 */
export function splitUrl(url: string): SplitUrlResult {
  const [address, paramsPart] = url.split('?');
  const params: Record<string, string> = paramsPart ? paramsPart.split('&').reduce((acc: Record<string, string>, currentValue: string) => {
    const [key, value] = currentValue.split('=');
    acc[key] = value;
    return acc;
  }, {}) : {};
  return { address, params };
}


/**
 * URL을 만드는 함수
 * @param address
 * @param params
 */
export function buildUrl(address: string, params: Record<string, string>): string {
  const paramsPart: string = Object.entries(params).map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`).join('&');
  return `${address}?${paramsPart}`;
}


/**
 * 두 음성 파일 이름이 같은지 확인하는 함수 (확장자 무시, NFC로 정규화한 후 비교)
 * @param v1
 * @param v2
 */
export function IsEqualVoiceFile(v1: string, v2: string): boolean {
  const unescape = (str: string): string => decodeURIComponent(str);
  const normalize = (str: string): string => str.normalize('NFC');
  const removeExtension = (str: string): string => str.replace(/\.[^/.]+$/, '');
  const getFileName = (str: string): string => str.split('/').pop()?.split('\\').pop() || '';

  const processFileName = (str: string): string =>
    normalize(removeExtension(getFileName(unescape(str))));

  const fileName1 = processFileName(v1);
  const fileName2 = processFileName(v2);

  return fileName1 === fileName2;
}
